import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createSelector, select, Store } from '@ngrx/store';
import { has, isObject, toString } from 'lodash-es';
import { filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { selectMfaCode, selectTokens, selectUser, selectUserToken, Tokens } from 'src/app/core/user/user.selectors';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import {
	APPCONFIG_BOOKINGS,
	APPCONFIG_CONTRACT_VIEWER,
	APPCONFIG_SW_TICKET_USAGE_REFRESH_TIME,
} from 'src/app/shared/enums/application-config.enums';
import {
	QUERY_PARAM_CARD_INDEX,
	QUERY_PARAM_ETOKEN,
	QUERY_PARAM_OTOKEN,
	QUERY_PARAM_USER_ID_TOKEN,
	QUERY_PARAM_UT_TOKEN,
} from 'src/app/shared/enums/query-param.enums';
import { Dictionary } from 'src/app/shared/types/dictionary';
import { validString } from 'src/app/shared/utilities/types.utils';
import { bookingReceived, resetSwFailedCount } from '../../pages/booking-portal/redux/bookings.actions';
import {
	updateCTokenAction,
	updateEmailAction,
	updateETokenAction,
	updateOrderIdAction,
	updateOTokenAction,
} from '../../pages/recover/recover.actions';
import {
	selectRecoverEmail,
	selectRecoverEToken,
	selectRecoverOrderId,
	selectRecoverOToken,
} from '../../pages/recover/recover.selectors';
import { selectLanguage } from '../client-configuration/client-configuration.selectors';
import { LocaleService } from '../locale/locale.service';
import { LoggerService } from '../logger/logger.service';
import { selectMerchantDetails } from '../merchant-details/merchant-details.selectors';
import { showNotificationAction } from '../notification/notification.actions';
import { NotificationType } from '../notification/notification.state';
import { hidePageSpinner } from '../page-spinner/page-spinner.actions';
import { routeTo, routeToWithOptions } from '../routing/routing.actions';
import {
	getOrderFormInfoSuccess,
	guestViewOrderComplete,
	isSessionLockoutAction,
	logout,
	openChangeUserEmailDialog,
	openVerifyBarcodeDialog,
	setMfaCodeAction,
	setUserTokenAction,
	startTimerAction,
	stopTimerAction,
	updateOrderTicketTypes,
	updateUserIdTokenAction,
	updateUTTokenAction,
} from '../user/user.actions';
import { apiResponse, postApiRequest } from './api.actions';
import { ApiServiceResponse, ApiStatus } from './api.models';
import { ApiService } from './api.service';
import { responseRequestType } from './api.utilities';
import { BookingPortalLogin } from './responses/booking-portal-login';
import { LoginInfo, PaymentPortalLoginResponseAction } from './responses/payment-portal-login';
import { RefreshMfaResponseAction } from './responses/refresh-mfa';
import { selectBookingsState } from 'src/app/pages/booking-portal/redux/bookings.selectors';
import { selectCartId } from 'src/app/feature/cart/redux/cart.selectors';
import { removeCartInfo } from 'src/app/feature/cart/redux/cart.actions';
import { Subject } from 'rxjs';

const { FAILED } = ApiStatus;

/**
 * Handles all the API requests and parsing the responses.
 */
@Injectable({
	providedIn: 'root',
})
export class ApiEffects implements OnDestroy {
	/**
	 * destroy subject for cleanup
	 */
	private _destroy$ = new Subject<void>();
	/**
	 * Are we a booking with a guest view order request?
	 * @private
	 */
	private _bookingGuestViewOrderRequested = false;
	/**
	 * Are we in an error state?
	 * @private
	 */
	private _isErrorState = false;
	/**
	 * Does this order have a payment plan?
	 * @private
	 */
	private _isPaymentPlan: boolean;
	/**
	 * Is the user's email on the capture list?
	 * @private
	 */
	private _isCaptureListEmail: boolean;
	/**
	 * Does this order have bookable entitlements?
	 * @private
	 */
	private _isBookable: boolean;
	/**
	 * Does this order have LoQ bookable entitlements?
	 * @private
	 */
	private _isLoqBookable: boolean;
	/**
	 * Are we in the Add/Edit Booking flow?
	 * @private
	 */
	private _isModifiedBooking: boolean;
	/**
	 * Does this order have a membership?
	 * @private
	 */
	private _isMembership: boolean;
	/**
	 * Is new contract viewer enabled?
	 * @private
	 */
	private _isNewContractViewer: boolean;
	/**
	 * Did the user use a capture prompt email?
	 * @private
	 */
	private _isCaptureEmailFlow: boolean;
	/**
	 * The MFA code if one was set.
	 * @private
	 */
	private _mfaCode: string;
	/**
	 * The user_token code if one was set. This comes from Passport Support or Passport Control
	 * @private
	 */
	private _userToken: string;
	/**
	 * The user tokens.
	 * @private
	 */
	private _tokens: Tokens;
	/**
	 * uidToken passed from PSP via URL
	 */
	private _uidToken: string;
	/**
	 * Allow Lock Out Access Config
	 * @private
	 */
	private _lockoutAccess: string;
	/**
	 * Allow lockout payment access for these codes
	 * @private
	 */
	private _lockoutPaymentCodes;
	/**
	 * Handles all requests to POST requests to our backend API
	 */
	postApiRequest$ = createEffect(() =>
		this._actions$.pipe(
			ofType(postApiRequest),
			withLatestFrom(
				this._store$.select(selectUser),
				this._store$.select(selectRecoverEToken),
				this._store$.select(selectRecoverOToken),
				this._store$.select(selectRecoverOrderId),
				this._store$.select(selectLanguage)
			),
			mergeMap(([action, user, eToken, oToken, orderId, clientLanguage]) => {
				const { requestType } = action;
				const { _userToken, _mfaCode } = this;
				const language: string = clientLanguage ?? 'en-US';
				let params;
				let customData;

				if (action?.customData) {
					customData = action.customData;
				}

				if (action?.params) {
					params = action.params;
				}

				if ([ApiRequestType.UNIFIED_GUEST_LOGIN].includes(requestType) && params?.order_id) {
					this._store$.dispatch(updateOrderIdAction({ order_id: params.order_id }));
				}

				if ([ApiRequestType.UNIFIED_GUEST_LOGIN].includes(requestType) && params?.email) {
					this._store$.dispatch(updateEmailAction({ email: params.email }));
				}

				if (
					[ApiRequestType.UNIFIED_GUEST_LOGIN].includes(requestType) &&
					params?.barcode &&
					params?.order_id &&
					params?.email
				) {
					// If this request comes with a barcode, orderId, and email, the user is in the capture email flow
					this._isCaptureEmailFlow = true;
				}

				if (requestType === ApiRequestType.ADD_EDIT_BOOKING) {
					this._isModifiedBooking = true;
					this._store$.dispatch(guestViewOrderComplete({ complete: false }));
				}

				// We need to always set the MFA code if we're calling PaymentPortalLogin and an MFA code exists
				if (
					[
						ApiRequestType.PAYMENT_PORTAL_LOGIN,
						ApiRequestType.GUEST_VIEW_ORDER,
						ApiRequestType.UNIFIED_GUEST_LOGIN,
					].includes(requestType) &&
					params
				) {
					if (validString(_mfaCode) && !validString(params.mfa_code)) {
						params = {
							...params,
							mfa_code: _mfaCode,
						};
					} else if (
						validString(_userToken) &&
						!validString(params.user_token) &&
						requestType !== ApiRequestType.PAYMENT_PORTAL_LOGIN &&
						requestType !== ApiRequestType.UNIFIED_GUEST_LOGIN
					) {
						// add user_token for subsequent calls
						params = {
							...params,
							user_token: _userToken,
						};
					}
				}

				if (requestType === ApiRequestType.UNIFIED_GUEST_LOGIN && params) {
					// add language to make sure any emails are in the correct language
					params = {
						...params,
						language,
					};
				}

				if (requestType !== ApiRequestType.PAYMENT_PORTAL_LOGIN && params) {
					// add user_id for subsequent calls
					params = {
						...params,
						user_id: 5,
					};
				}

				if (requestType === ApiRequestType.PAYMENT_PORTAL_LOGIN && params && this._lockoutAccess) {
					params = {
						...params,
						allow_lock_out_access: 1,
					};
				}

				if (requestType === ApiRequestType.PAYMENT_PORTAL_LOGIN && params && !params.oToken && oToken) {
					params = {
						...params,
						oToken,
					};
				}

				if (requestType === ApiRequestType.PAYMENT_PORTAL_LOGIN && params && !params.eToken && eToken) {
					params = {
						...params,
						eToken,
					};
				}

				if (
					(requestType === ApiRequestType.PAYMENT_PORTAL_LOGIN ||
						requestType === ApiRequestType.ADD_EDIT_BOOKING ||
						requestType === ApiRequestType.EMAIL_ITINERARY) &&
					params &&
					!params.order_id &&
					(orderId || user?.user?.orderNumber)
				) {
					params = {
						...params,
						order_id: orderId ?? user?.user?.orderNumber,
					};
				}

				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(requestType, params, action.useGlobal)
					.pipe(
						switchMap((response) => {
							if (!has(response, 'SERVICE') || !isObject(response.SERVICE)) {
								this._logger.error(`[Api Error] response was not an object`, response);

								return [
									apiResponse({
										isOk: false,
										status: ApiStatus.FAILED,
										requestType,
										response: {
											error_code: '-1',
											error_msg: 'Response from backend was not an object',
											request_type: requestType,
										},
										params,
										customData,
									}),
								];
							}

							const { status } = response.SERVICE;

							return [
								apiResponse({
									isOk: status === ApiStatus.OK,
									status,
									requestType,
									response: response.SERVICE,
									params,
									customData,
								}),
							];
						})
					);
			})
		)
	);

	onLogoutResetApi$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(logout),
				map(() => {
					this._isCaptureEmailFlow = false;
					this._localStorage.removeItem('dest');
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles response from BookingPortalLogin
	 */
	onBookingPortalLogin$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.BOOKING_PORTAL_LOGIN)),
				withLatestFrom(
					this._store$.select(selectAppConfig([APPCONFIG_BOOKINGS, APPCONFIG_SW_TICKET_USAGE_REFRESH_TIME])),
					this._store$.select(selectRecoverEmail),
					this._store$.select(selectBookingsState),
					this._store$.select(selectCartId)
				),
				map(([action, appConfigs, email, bookings, cartId]) => {
					this.storeCodesAndTokens(action);
					const bookingResponse = action.response as BookingPortalLogin;
					if (action.isOk) {
						let swFailedCount;
						const deepLink = this._localStorage.getItem<string>('dest')?.split('/');
						const extras: Dictionary<any> = [
							QUERY_PARAM_OTOKEN,
							QUERY_PARAM_ETOKEN,
							'order_id',
							QUERY_PARAM_UT_TOKEN,
							QUERY_PARAM_USER_ID_TOKEN,
						].reduce((acc: Dictionary<string>, key) => {
							const value = action.params[key];
							if (validString(value)) {
								acc[key] = value;
							}

							return acc;
						}, {});

						/**
						 * If Bookings only make GuestViewOrder
						 * for name information in summary header
						 */
						if (!this._isPaymentPlan && !this._isMembership) {
							this._bookingGuestViewOrderRequested = true;
							this._store$.dispatch(
								postApiRequest({
									requestType: ApiRequestType.GUEST_VIEW_ORDER,
									params: {
										...extras,
										cToken: bookingResponse.cToken,
									},
								})
							);
						}

						/**
						 * If the config is set and the ticket usage error is present
						 * wait configured time and retry the BPL call
						 * count the failures, max retry attempts is 2
						 */
						if (
							appConfigs.sw_ticket_usage_refresh_time &&
							bookingResponse?.TICKETS?.[0]?.TICKET?.[0]?.sw_error_code === '750012' &&
							bookingResponse?.TICKETS?.[0]?.TICKET?.[0]?.sw_error_message
								?.toLocaleLowerCase()
								.includes('ticket not found')
						) {
							swFailedCount = bookings?.swFailedCount ? bookings?.swFailedCount + 1 : 1;
							const waitTime = appConfigs.sw_ticket_usage_refresh_time;

							if (swFailedCount < 3) {
								setTimeout(() => {
									this._store$.dispatch(
										postApiRequest({
											requestType: ApiRequestType.BOOKING_PORTAL_LOGIN,
											params: {
												...extras,
												email: email,
												cToken: bookingResponse.cToken,
											},
										})
									);
								}, waitTime);
							}
						} else if (
							appConfigs.sw_ticket_usage_refresh_time &&
							!bookingResponse?.TICKETS?.[0]?.TICKET?.[0]?.sw_error_code &&
							!bookingResponse?.TICKETS?.[0]?.TICKET?.[0]?.sw_error_message &&
							bookings?.swFailedCount
						) {
							// reset the swFailedCount if the BPL call is successful
							this._store$.dispatch(resetSwFailedCount());
						}

						this._store$.dispatch(updateOTokenAction({ oToken: bookingResponse.oToken }));
						this._store$.dispatch(updateCTokenAction({ cToken: bookingResponse.cToken }));
						this._store$.dispatch(
							bookingReceived({
								orderId: bookingResponse.order_id,
								tickets: bookingResponse.TICKETS?.[0]?.TICKET ?? [],
								...(swFailedCount > 0 && { swFailedCount: swFailedCount }),
							})
						);
						if (this._isModifiedBooking || (!this._isPaymentPlan && !this._isMembership)) {
							this._isModifiedBooking = false;
							const emailCaptureList = appConfigs?.bookings?.capture_prompt_email_list ?? [];

							if (cartId) {
								// Remove cartId now that purchase is complete
								this._store$.dispatch(removeCartInfo());
							}

							if (emailCaptureList.includes(email)) {
								this._store$.dispatch(openChangeUserEmailDialog());
							} else {
								if (!this._bookingGuestViewOrderRequested) {
									const params = deepLink ?? ['booking-portal', 'summary'];
									this._localStorage.removeItem('dest');
									this._store$.dispatch(
										routeTo({
											payload: params,
										})
									);
								}
							}
						}
					} else if (!action.customData?.suppressModal) {
						if (!this._isPaymentPlan && !this._isMembership) {
							this._store$.dispatch(logout());
						}
						this._store$.dispatch(
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `Login: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							})
						);
					} else {
						this._store$.dispatch(logout());
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles response from PaymentPortalLogin
	 * Saves MFA information if it was on the request and successful
	 * Calls GuestViewOrder if PaymentPortalLogin is successful
	 */
	onPaymentPortalLogin$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.PAYMENT_PORTAL_LOGIN)),
				map((action) => {
					if (action.isOk) {
						this.storeCodesAndTokens(action);

						const extras: Dictionary<any> = [
							QUERY_PARAM_OTOKEN,
							QUERY_PARAM_ETOKEN,
							'order_id',
							QUERY_PARAM_UT_TOKEN,
							QUERY_PARAM_USER_ID_TOKEN,
						].reduce((acc: Dictionary<string>, key) => {
							const value = action.params[key];
							if (validString(value)) {
								acc[key] = value;
							}

							return acc;
						}, {});

						/**
						 * If in lockout access flow and want limited access
						 * Manage Payments and Change Card unavailable
						 * update state for limited functionality
						 */
						if (action.response?.lock_out_error_code) {
							if (
								this._lockoutPaymentCodes &&
								this._lockoutPaymentCodes.includes(action.response?.lock_out_error_code)
							) {
								this._store$.dispatch(isSessionLockoutAction({ isLockout: false }));
							} else {
								this._store$.dispatch(isSessionLockoutAction({ isLockout: true }));
								// Using error 84 message for all situations when the user is in lockout flow without payments available
								// automated billing cycle (84)
								this._store$.dispatch(
									showNotificationAction({
										buttonLabel: this._locale.get('common.close'),
										dialogType: NotificationType.GENERAL,
										initiator: `Login: Error Code: 84`,
										message: this._locale.get('common.errorcode84notification'),
									})
								);
							}
						}

						this._store$.dispatch(
							postApiRequest({
								requestType: ApiRequestType.GUEST_VIEW_ORDER,
								params: {
									...extras,
									cToken: action.response.CUSTOMER_DETAILS_REPLY[0].CUSTOMER[0].cToken,
								},
								customData: {
									enableRouting: !action.customData?.disableRouting,
									threeDSParams: action.customData?.threeDSParams,
								},
							})
						);
					} else if (action.response.http_response_code === '401') {
						this._store$.dispatch(routeTo({ payload: ['verify'] }));
					} else if (!action.customData?.suppressModal) {
						this._store$.dispatch(
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `Login: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							})
						);
					} else {
						this._store$.dispatch(logout());
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles response from UnifiedGuestLogin
	 * Saves MFA information if it was on the request and successful
	 * Calls GetUnifiedOrderTicketType to determine which calls need to be made
	 * next in order to retrieve necessary data
	 */
	onUnifiedGuestLogin$ = createEffect(() =>
		this._actions$.pipe(
			ofType(apiResponse),
			filter(responseRequestType(ApiRequestType.UNIFIED_GUEST_LOGIN)),
			withLatestFrom(
				this._store$.pipe(select(selectRecoverEmail)),
				this._store$.pipe(select(selectRecoverOrderId)),
				this._store$.pipe(select(selectAppConfig([APPCONFIG_BOOKINGS])))
			),
			map(([action, email, orderId, config]) => {
				const captureEmailList = config?.bookings?.capture_prompt_email_list ?? [];

				if (action.isOk) {
					this.storeCodesAndTokens(action);
					if (action.response?.eToken) {
						this._store$.dispatch(updateETokenAction({ eToken: action.response?.eToken }));
					}

					const params = {
						eToken: action?.params?.eToken ?? action.response?.eToken,
						oToken: action.params.oToken,
						jwt: action.response?.jwt,
						email,
						order_id: orderId,
					};

					return postApiRequest({
						requestType: ApiRequestType.GET_UNIFIED_ORDER_TICKET_TYPE,
						params,
						customData: {
							suppressModal: true,
						},
					});
				} else if (action.response.http_response_code === '401') {
					// if the email address used here is on the capture list, request the last 4 of their barcode
					if (captureEmailList.includes(email) && !this._isCaptureEmailFlow) {
						this._isCaptureEmailFlow = true;
						this._store$.dispatch(updateEmailAction({ email }));
						this._store$.dispatch(updateOrderIdAction({ order_id: orderId }));
						this._store$.dispatch(openVerifyBarcodeDialog());
						return routeTo({ payload: ['login'] });
					}

					if (this._isCaptureEmailFlow) {
						this._isCaptureEmailFlow = false;
						return showNotificationAction({
							buttonLabel: this._locale.get('common.close'),
							dialogType: NotificationType.GENERAL,
							initiator: `Login: barcode incorrect`,
							message: this._locale.get(`errorcode.401404`),
						});
					} else {
						this.storeCodesAndTokens(action);
						if (action.response?.eToken) {
							this._store$.dispatch(updateETokenAction({ eToken: action.response?.eToken }));
						}
						return routeTo({ payload: ['verify'] });
					}
				} else if (!action.customData?.suppressModal) {
					return showNotificationAction({
						buttonLabel: this._locale.get('common.close'),
						dialogType: NotificationType.GENERAL,
						initiator: `Login: Failed`,
						message: this._locale.get(`errorcode.${action.response.error_code}`),
					});
				} else {
					return logout();
				}
			})
		)
	);

	/**
	 * Handles response from GetUnifiedOrderTicketType
	 * Makes additional calls needed in order to retrieve necessary data based on flags in the response
	 * only email & orderId are needed for login
	 * eToken & oToken are needed for auto-login flow
	 */
	onGetUnifiedOrderTicketType$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.GET_UNIFIED_ORDER_TICKET_TYPE)),
				withLatestFrom(
					this._store$.select(selectRecoverEmail),
					this._store$.select(selectRecoverOrderId),
					this._store$.select(selectRecoverEToken),
					this._store$.select(selectRecoverOToken),
					this._store$.select(selectAppConfig([APPCONFIG_BOOKINGS, APPCONFIG_CONTRACT_VIEWER]))
				),
				map(([action, email, orderId, eToken, oToken, configs]) => {
					if (action.isOk) {
						const params = {
							jwt: action.response?.jwt,
							email,
							order_id: orderId,
							...(!email && { eToken }),
							...(!orderId && { oToken }),
						};

						// Set values for accuracy for each order login
						this._isPaymentPlan = false;
						this._isBookable = false;
						this._isLoqBookable = false;
						this._isMembership = false;
						this._isErrorState = false;
						this._isCaptureListEmail = false;
						this._isNewContractViewer = false;

						if (action.response.is_payment_plan === '1') {
							this._isPaymentPlan = true;
							this._store$.dispatch(
								postApiRequest({
									requestType: ApiRequestType.PAYMENT_PORTAL_LOGIN,
									params,
									customData: {
										suppressModal: true,
									},
								})
							);
						}
						if (action.response.is_bookable === '1') {
							if (configs?.bookings?.enableBookings) {
								// bookings are enabled, make BPL call and set bookable to true
								this._isBookable = true;

								this._store$.dispatch(
									postApiRequest({
										requestType: ApiRequestType.BOOKING_PORTAL_LOGIN,
										params,
									})
								);
							} else if (!this._isPaymentPlan) {
								this._isErrorState = true;
								// bookings are not enable, order doesn't have payment play, show error
								this._store$.dispatch(hidePageSpinner({ initiator: 'Bookings: invalid setup' }));
								this._store$.dispatch(
									routeTo({
										payload: ['login'],
									})
								);
								setTimeout(() => {
									this._store$.dispatch(
										showNotificationAction({
											buttonLabel: this._locale.get('common.close'),
											dialogType: NotificationType.GENERAL,
											initiator: `Bookings: invalid setup `,
											message: this._locale.get(`login.disabledbookingmessage`),
										})
									);
									this._store$.dispatch(stopTimerAction());
								}, 500);
							}
						}

						if (action.response.is_loq_bookable === '1') {
							this._isLoqBookable = true;
							// todo: dispatch action to make appropriate request for LoQ bookable orders
						}

						if (action.response.is_membership === '1') {
							this._isMembership = true;
							// todo: dispatch action to make appropriate request for membership orders
						}

						if (configs?.contract_viewer?.enable && configs?.contract_viewer?.enable_new_contract_viewer) {
							this._isNewContractViewer = true;
						}

						if (this._isBookable || this._isPaymentPlan || this._isMembership || this._isLoqBookable) {
							this._store$.dispatch(
								updateOrderTicketTypes({
									isPaymentPlan: this._isPaymentPlan,
									isBookable: this._isBookable,
									isLoqBookable: this._isLoqBookable,
									isMembership: this._isMembership,
									isNewContractViewer: this._isNewContractViewer,
								})
							);
						} else {
							if (!this._isErrorState) {
								this._isErrorState = true;
								this._store$.dispatch(logout());

								setTimeout(() => {
									this._store$.dispatch(
										showNotificationAction({
											buttonLabel: this._locale.get('common.close'),
											dialogType: NotificationType.GENERAL,
											initiator: `Login: no supported ticket types`,
											message: this._locale.get(`login.nosupportedtickettypes`),
										})
									);
									this._store$.dispatch(stopTimerAction());
								}, 500);
							}
						}
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles GuestViewOrder response
	 */
	onGuestViewOrder$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.GUEST_VIEW_ORDER)),
				withLatestFrom(
					this._store$.pipe(select(selectAppConfig([APPCONFIG_CONTRACT_VIEWER]))),
					this._store$.pipe(select(selectMerchantDetails))
				),
				map(([action, contractViewer, merchantDetials]) => {
					if (action.isOk) {
						const deepLink = this._localStorage.getItem<string>('dest')?.split('/');

						if (
							action.response.VIEW_ORDER_REPLY?.[0]?.ORDER_STATUS_REPLY?.[0]?.merchant_id !==
							merchantDetials?.merchantId
						) {
							// Need to call getMerchantDetails for the merchant the order was placed with
							this._store$.dispatch(
								postApiRequest({
									requestType: ApiRequestType.GET_MERCHANT_DETAILS,
									params: {
										merchant_id:
											action.response.VIEW_ORDER_REPLY[0].ORDER_STATUS_REPLY[0].merchant_id,
									},
								})
							);
						}
						this._store$.dispatch(guestViewOrderComplete({ complete: true }));

						// If enable_new_contract_viewer is enabled, call getOrderFormInfo for contract details
						if (
							contractViewer?.contract_viewer?.enable &&
							contractViewer?.contract_viewer?.enable_new_contract_viewer
						) {
							this._store$.dispatch(
								postApiRequest({
									requestType: ApiRequestType.GET_ORDER_FORM_INFO,
									params: {
										cToken: action.response.VIEW_ORDER_REPLY[0].CUSTOMER_DETAILS_REPLY[0]
											.CUSTOMER[0].cToken,
										oToken: action.response.VIEW_ORDER_REPLY[0].ORDER_STATUS_REPLY[0].oToken,
										formDetails: 'Y',
									},
								})
							);
						}

						if (action.customData?.threeDSParams) {
							const { threeDSParams } = action.customData;

							this._store$.dispatch(
								routeToWithOptions({
									routeParams: [
										'account',
										'payment',
										'accesso-pay',
										threeDSParams[QUERY_PARAM_CARD_INDEX],
									],
									options: {
										queryParams: threeDSParams,
									},
								})
							);
						} else if (action.customData?.enableRouting) {
							const params = deepLink ?? ['account', 'overview'];
							this._store$.dispatch(
								routeTo({
									payload: params,
								})
							);
						}

						if (this._bookingGuestViewOrderRequested) {
							this._bookingGuestViewOrderRequested = false;
							const params =
								deepLink && (!deepLink.includes('booking-portal') || !deepLink.includes('upgrade'))
									? deepLink
									: ['booking-portal', 'summary'];
							this._localStorage.removeItem('dest');
							this._store$.dispatch(
								routeTo({
									payload: params,
								})
							);
						}
					} else if (!action.customData?.suppressModal) {
						[
							logout(),
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `GuestViewOrder: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							}),
						].forEach((dispatchAction) => this._store$.dispatch(dispatchAction));
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles RefreshMfa response
	 * Dispatches action to store new MFA information and restart MFA timer
	 */
	refreshMfaResponse$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.REFRESH_MFA)),
				map((action) => {
					if (action.isOk) {
						this.storeCodesAndTokens(action, true);
					} else {
						[
							logout(),
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `Refresh MFA: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							}),
						].forEach((dispatchAction) => this._store$.dispatch(dispatchAction));
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles changeCard response
	 * If successful calls paymentPortalLogin to get updated information
	 */
	changeCardResponse$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.CHANGE_CARD)),
				map((action) => {
					if (action.isOk) {
						if (action.customData?.disableLoginCall) {
							return;
						}

						const params = {
							eToken: this._tokens.eToken,
							oToken: this._tokens.oToken,
							uidToken: this._tokens.uidToken ?? '',
							utToken: this._tokens.utToken ?? '',
						};

						this._store$.dispatch(
							postApiRequest({
								requestType: ApiRequestType.PAYMENT_PORTAL_LOGIN,
								params,
								customData: {
									suppressModal: true,
									disableRouting: true,
								},
							})
						);
					} else {
						this._store$.dispatch(hidePageSpinner({ initiator: 'changeCard: changeCard failed' }));
						this._store$.dispatch(
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `ChangeCard: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							})
						);
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles response from GetOrderFormInfo
	 */
	orderFormInfoResponse$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.GET_ORDER_FORM_INFO)),
				map((action) => {
					if (action.isOk) {
						if (action.response.FORMS?.[0]?.FORM?.length > 0) {
							const forms = action.response?.FORMS?.[0]?.FORM;
							const order = action.response?.ORDER;
							const paymentSchedule = action.response?.PAYMENT_SCHEDULE?.[0]?.PS;

							let formDisplayDetails = [];

							forms?.forEach((form, index) => {
								let name = form?.name;
								let timestamp = form?.F?.find((f) => f.name === 'timestamp')?.value;
								let formType;
								const tilaMembership =
									form?.F?.find((f) => f.name === 'tilaMembershipFormName')?.value ?? '';

								if (tilaMembership && tilaMembership === name) {
									formType = 'tila_membership';
								} else if (form?.category === 'terms_conditions' || !action.response.PAYMENT_SCHEDULE) {
									formType = 'terms';
								} else {
									formType = 'tila';
								}

								let formDetails = {
									name: name,
									timestamp: timestamp,
									type: formType,
									formInfo: form.F,
									orderInfo: order,
									contractIndex: index + 1,
									language: form?.language,
									version: form?.version,
									active: form?.active,
									id: form?.id,
									...(paymentSchedule && { paymentSchedule: paymentSchedule }),
								};
								formDisplayDetails.push(formDetails);
							});

							this._isNewContractViewer = true;

							const orderFormInfo = {
								FORMS: forms,
								ORDER: order,
								...(paymentSchedule && { PAYMENT_SCHEDULE: paymentSchedule }),
								displayDetails: formDisplayDetails,
							};

							this._store$.dispatch(getOrderFormInfoSuccess({ orderFormInfo }));
						} else {
							/**
							 *  Show nothing to the user as it could mean no contract exists
							 * Shouldn't happen to new orders, but potiential for bugs or historical orders
							 * Route user to Overview is default unless there is deep link dest in local storage
							 * https://accesso.atlassian.net/browse/PORTAL-2521
							 **/
							this._isNewContractViewer = false;
						}
					} else {
						this._isNewContractViewer = false;
					}
					this._store$.dispatch(
						updateOrderTicketTypes({
							isPaymentPlan: this._isPaymentPlan,
							isBookable: this._isBookable,
							isLoqBookable: this._isLoqBookable,
							isMembership: this._isMembership,
							isNewContractViewer: this._isNewContractViewer,
						})
					);
				})
			),
		{ dispatch: false }
	);
	/**
	 * Builds a standard error response object
	 * @param requestType The request type
	 * @param err The error we'll be stringifying
	 */
	buildStandardErrorResponse(requestType: string, err: any): ApiServiceResponse {
		return {
			error_code: '-1',
			error_msg: toString(err),
			request_type: requestType,
			status: FAILED,
		};
	}

	/**
	 * Clean 'er up!
	 */
	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Sets MFA code and expiration if it was on the request and the response was good
	 * @param action action that fired the request
	 * @param refresh true if envoked from RefreshMfa
	 */
	private storeCodesAndTokens(
		action: RefreshMfaResponseAction | PaymentPortalLoginResponseAction,
		refresh = false
	): void {
		// refreshMFA response has new mfa_code in response node
		let code;
		let userToken;
		let uidToken;
		let utToken;

		if (refresh) {
			code = action.response?.mfa_code;
		} else {
			const params = action.params as LoginInfo;

			userToken = params?.user_token;
			code = params?.mfa_code;
			uidToken = params?.uidToken;
			utToken = params?.utToken;
		}

		let expiration = action.response?.mfa_expiration;

		// store encrypted tokens for PPL request
		if (validString(uidToken)) {
			this._uidToken = uidToken;
			this._store$.dispatch(updateUserIdTokenAction({ uidToken }));
		}

		if (validString(utToken)) {
			this._store$.dispatch(updateUTTokenAction({ utToken }));
		}

		if (validString(code) && validString(expiration)) {
			expiration = new Date(expiration);
			this._store$.dispatch(setMfaCodeAction({ code, expiration }));
			this._store$.dispatch(startTimerAction({ isMFA: true }));
		} else if (validString(userToken)) {
			this._store$.dispatch(setUserTokenAction({ userToken }));
		} else {
			this._store$.dispatch(startTimerAction({ isMFA: false }));
		}
	}

	/**
	 * @param _actions$ Actions Observable stream
	 * @param _apiService Api Service
	 * @param _logger Logger Service
	 * @param _store$ Global store
	 * @param _locale the locale service
	 * @param _localStorage
	 */
	constructor(
		private _actions$: Actions,
		private _apiService: ApiService,
		private _logger: LoggerService,
		private _store$: Store<AppState>,
		private _locale: LocaleService,
		private _localStorage: LocalStorageService
	) {
		this._store$
			.pipe(
				select(
					createSelector(
						selectTokens,
						selectMfaCode,
						selectUserToken,
						selectAppConfig(['allow_lock_out_access']),
						selectAppConfig(['enable_payments_lock_out_codes']),
						(tokens, mfaCode, userToken, lockOutConfig, lockOutPaymentCodes) => {
							return {
								tokens,
								mfaCode,
								userToken,
								lockOutConfig,
								lockOutPaymentCodes,
							};
						}
					)
				),
				takeUntil(this._destroy$)
			)
			.subscribe(({ tokens, mfaCode, userToken, lockOutConfig, lockOutPaymentCodes }) => {
				this._tokens = tokens;
				this._mfaCode = mfaCode;
				this._userToken = userToken;
				this._lockoutPaymentCodes = lockOutPaymentCodes?.enable_payments_lock_out_codes;
				const lockOutAccessConfig = lockOutConfig?.allow_lock_out_access;
				if (validString(this._lockoutPaymentCodes)) {
					this._lockoutPaymentCodes = this._lockoutPaymentCodes.split(',');
				}

				if ((lockOutAccessConfig === undefined || lockOutAccessConfig === null) && this._uidToken) {
					// Allow lockout access from PSP if config is not set
					this._lockoutAccess = 'true';
				} else {
					this._lockoutAccess = lockOutAccessConfig;
				}
			});
	}
}
